import type { CategoryDefDto, FieldDef, LookupListDto } from '@tww/shared';
import { fmtDate } from '@tww/shared';
import { Checkbox, DateInput, MultiSelect, NumberInput, Select, TextArea, TextInput } from '../../ui/fields';

/** Label eines Attributwerts (Codes aus Stammlisten werden aufgelöst) */
export function attrLabel(field: FieldDef | undefined, value: unknown, lookups: LookupListDto[] | undefined): string {
  if (value === undefined || value === null || value === '') return '';
  if (!field) return Array.isArray(value) ? value.join(', ') : String(value);
  const resolve = (v: unknown) => {
    if (field.lookup) {
      const l = lookups?.find((x) => x.key === field.lookup);
      return l?.values?.find((x) => x.code === String(v))?.label ?? String(v);
    }
    return String(v);
  };
  switch (field.type) {
    case 'bool':
      return value ? 'ja' : 'nein';
    case 'date':
      return fmtDate(String(value));
    case 'multiselect':
      return (Array.isArray(value) ? value : [value]).map(resolve).join(', ');
    case 'number':
      return Number(value).toLocaleString('de-DE');
    default:
      return resolve(value);
  }
}

function optionsOf(field: FieldDef, lookups: LookupListDto[] | undefined) {
  if (field.lookup) {
    const l = lookups?.find((x) => x.key === field.lookup);
    return (l?.values ?? []).filter((v) => v.is_active).map((v) => ({ value: v.code, label: v.label }));
  }
  return (field.options ?? []).map((o) => ({ value: o, label: o }));
}

/** Dynamische Zusatzfelder einer Kategorie */
export function AttributeFields({
  def,
  value,
  onChange,
  errors,
  lookups,
}: {
  def: CategoryDefDto;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  errors?: Record<string, string>;
  lookups: LookupListDto[] | undefined;
}) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  const sections = [...new Set(def.fields.map((f) => f.section ?? ''))];
  return (
    <>
      {sections.map((sec) => (
        <div key={sec} className="full">
          {sec && <h3 style={{ margin: '6px 0 2px', fontSize: 13.5, color: 'var(--ink-2)' }}>{sec}</h3>}
          <div className="form">
            {def.fields
              .filter((f) => (f.section ?? '') === sec)
              .map((f) => {
                const err = errors?.[f.key];
                const common = { label: f.label, hint: f.help, error: err, required: f.required };
                const v = value[f.key];
                switch (f.type) {
                  case 'text':
                    return <TextInput key={f.key} {...common} value={v == null ? '' : String(v)} onChange={(x) => set(f.key, x)} />;
                  case 'textarea':
                    return <TextArea key={f.key} {...common} value={v == null ? '' : String(v)} onChange={(x) => set(f.key, x)} rows={2} />;
                  case 'number':
                    return <NumberInput key={f.key} {...common} value={v == null || v === '' ? null : Number(v)} onChange={(x) => set(f.key, x)} />;
                  case 'bool':
                    return <Checkbox key={f.key} label={f.label} hint={f.help} value={Boolean(v)} onChange={(x) => set(f.key, x)} />;
                  case 'date':
                    return <DateInput key={f.key} {...common} value={v == null ? '' : String(v)} onChange={(x) => set(f.key, x)} />;
                  case 'select':
                    return <Select key={f.key} {...common} value={v == null ? '' : String(v)} onChange={(x) => set(f.key, x)} options={optionsOf(f, lookups)} />;
                  case 'multiselect':
                    return <MultiSelect key={f.key} {...common} value={Array.isArray(v) ? v.map(String) : []} onChange={(x) => set(f.key, x)} options={optionsOf(f, lookups)} full={false} />;
                  default:
                    return null;
                }
              })}
          </div>
        </div>
      ))}
    </>
  );
}
